import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useAutomationLogs, useAutomations } from '@/hooks/queries/dm-automation';
import { useInstagramStatus } from '@/hooks/queries/instagram';
import type { DmSendLogStatus } from '@/types/dmAutomation';
import { PageHeader, TRIGGER_LABEL } from './_shared';

const STATUS_OPTIONS: { key: 'ALL' | DmSendLogStatus; label: string }[] = [
    { key: 'ALL', label: '전체' },
    { key: 'SENT', label: '보냄' },
    { key: 'OPENED', label: '읽음' },
    { key: 'CLICKED', label: '버튼 눌림' },
    { key: 'FAILED', label: '실패' },
    { key: 'RATE_LIMITED', label: '한도 초과' },
    { key: 'WINDOW_EXPIRED', label: '24h 만료' },
    { key: 'PENDING_FOLLOW', label: '팔로우 대기' },
];

const STATUS_LABEL: Record<DmSendLogStatus, string> = {
    SENT: '보냄',
    OPENED: '읽음',
    CLICKED: '버튼 눌림',
    FAILED: '실패',
    RATE_LIMITED: '한도 초과',
    WINDOW_EXPIRED: '24h 만료',
    PENDING_FOLLOW: '팔로우 대기',
};

const STATUS_COLOR: Record<DmSendLogStatus, { bg: string; color: string }> = {
    SENT: { bg: 'rgba(33,150,243,0.12)', color: '#1976d2' },
    OPENED: { bg: 'rgba(76,175,80,0.12)', color: '#2e7d32' },
    CLICKED: { bg: 'rgba(251,109,72,0.15)', color: '#FB6D48' },
    FAILED: { bg: 'rgba(244,67,54,0.12)', color: '#d32f2f' },
    RATE_LIMITED: { bg: 'rgba(0,0,0,0.06)', color: 'rgba(0,0,0,0.6)' },
    WINDOW_EXPIRED: { bg: 'rgba(0,0,0,0.06)', color: 'rgba(0,0,0,0.6)' },
    PENDING_FOLLOW: { bg: 'rgba(255,193,7,0.18)', color: '#b26a00' },
};

const formatDateTime = (iso: string): string => {
    try {
        const d = new Date(iso);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
        return iso;
    }
};

const LIMIT = 20;

const SendLogsPage = () => {
    const statusQuery = useInstagramStatus({ withProfile: false });
    const isConnected = statusQuery.data?.isConnected === true;

    const automationsQuery = useAutomations({ enabled: isConnected });
    const automations = automationsQuery.data?.itemList ?? [];

    const [automationId, setAutomationId] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<'ALL' | DmSendLogStatus>('ALL');
    const [page, setPage] = useState(1);

    // 자동화 목록이 로드되면 첫 번째 항목 자동 선택
    useEffect(() => {
        if (!automationId && automations.length > 0) {
            setAutomationId(automations[0].id);
        }
    }, [automations, automationId]);

    // 자동화 변경 시 페이지 1로 리셋
    useEffect(() => {
        setPage(1);
    }, [automationId]);

    const logsQuery = useAutomationLogs(automationId, page, LIMIT, {
        enabled: Boolean(automationId),
    });

    const filteredItems = useMemo(() => {
        const items = logsQuery.data?.itemList ?? [];
        if (statusFilter === 'ALL') return items;
        return items.filter((l) => l.status === statusFilter);
    }, [logsQuery.data, statusFilter]);

    const totalPages = Math.max(1, Math.ceil((logsQuery.data?.totalCount ?? 0) / LIMIT));

    if (!isConnected) {
        return (
            <Box>
                <PageHeader title="보낸 기록" desc="자동으로 발송된 DM 내역을 확인합니다" />
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Instagram 계정을 연결하면 발송 로그를 확인할 수 있어요.
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader title="보낸 기록" desc="자동으로 발송된 DM 내역을 확인합니다" />

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{ mb: 2 }}
            >
                <FormControl
                    size="small"
                    sx={{
                        minWidth: { xs: '100%', md: 340 },
                        flex: { md: 1 },
                        maxWidth: { md: 480 },
                    }}
                >
                    <InputLabel id="automation-filter">자동화</InputLabel>
                    <Select
                        labelId="automation-filter"
                        label="자동화"
                        value={automationId}
                        onChange={(ev) => setAutomationId(ev.target.value as string)}
                        disabled={automationsQuery.isLoading || automations.length === 0}
                        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                        renderValue={(value) => {
                            const a = automations.find((x) => x.id === value);
                            if (!a) return '';
                            return (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.name}>
                                        {a.name}
                                    </Box>
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 10,
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                            px: 0.75,
                                            py: 0.25,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {TRIGGER_LABEL[a.triggerType]}
                                    </Box>
                                </Box>
                            );
                        }}
                    >
                        {automations.map((a) => (
                            <MenuItem key={a.id} value={a.id}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                        {a.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {TRIGGER_LABEL[a.triggerType]}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl
                    size="small"
                    sx={{
                        minWidth: { xs: '100%', md: 180 },
                    }}
                >
                    <InputLabel id="status-filter">상태</InputLabel>
                    <Select
                        labelId="status-filter"
                        label="상태"
                        value={statusFilter}
                        onChange={(ev) => setStatusFilter(ev.target.value as 'ALL' | DmSendLogStatus)}
                        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                    >
                        {STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s.key} value={s.key}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {automationsQuery.isLoading ? (
                <Skeleton variant="rounded" height={240} sx={{ borderRadius: 3 }} />
            ) : automations.length === 0 ? (
                <Card variant="outlined" sx={{ borderRadius: 3, borderStyle: 'dashed' }}>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            아직 자동화가 없어요
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            자동화를 만들면 발송 로그가 여기에 표시됩니다
                        </Typography>
                    </CardContent>
                </Card>
            ) : logsQuery.isError ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                    발송 로그를 불러오지 못했어요.
                </Alert>
            ) : (
                <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        전체 {logsQuery.data?.totalCount ?? 0}건
                        {statusFilter !== 'ALL' && ` · 이 페이지에서 ${filteredItems.length}건 표시`}
                    </Typography>

                    <TableContainer
                        component={Card}
                        variant="outlined"
                        sx={{ borderRadius: 3, overflowX: 'auto' }}
                    >
                        <Table size="small" sx={{ minWidth: 720 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>수신자 IG</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>트리거</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>발송 시각</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>읽음</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>클릭</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>상태</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logsQuery.isFetching && filteredItems.length === 0 ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <TableRow key={i}>
                                            {Array.from({ length: 6 }).map((_, j) => (
                                                <TableCell key={j}>
                                                    <Skeleton width="80%" />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : filteredItems.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {statusFilter === 'ALL'
                                                    ? '발송 기록이 없어요'
                                                    : `"${STATUS_LABEL[statusFilter]}" 상태의 기록이 없어요`}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredItems.map((log) => {
                                        const color = STATUS_COLOR[log.status];
                                        return (
                                            <TableRow key={log.id} hover>
                                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                    {log.recipientIgUserId}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                        {log.triggerSource}
                                                    </Typography>
                                                    {log.triggerComment && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ display: 'block', maxWidth: 240 }}
                                                            noWrap
                                                        >
                                                            "{log.triggerComment}"
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                    {formatDateTime(log.sentAt)}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                                                    {log.openedAt ? formatDateTime(log.openedAt) : '—'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                                                    {log.clickedAt ? formatDateTime(log.clickedAt) : '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={STATUS_LABEL[log.status]}
                                                        sx={{
                                                            height: 22,
                                                            fontWeight: 700,
                                                            fontSize: 10,
                                                            bgcolor: color.bg,
                                                            color: color.color,
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Stack direction="row" justifyContent="center" sx={{ mt: 2.5 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, p) => setPage(p)}
                                shape="rounded"
                                size="small"
                            />
                        </Stack>
                    )}
                </>
            )}
        </Box>
    );
};

export default SendLogsPage;
